import { PerDiemKind, PriceClassification, ProductUnit } from "~/gql/types2";

export type ProductRate = {
  id: number;
  productId: number;
  amount?: number | null;
};

export type Product = {
  id: number;
  name: string;
  classification: PriceClassification;
  unit: ProductUnit;
  // Set only on a `PerDiem` product -- which per diem it represents.
  perDiemKind?: PerDiemKind | null;
  rates: ProductRate[];
};

/**
 * Whether a table outside the catalog holds the product's amounts, leaving the
 * product to supply only identity. Such a product is minted by migration, is
 * not creatable, and cannot be renamed.
 */
export const isExternallySourced = (
  classification: PriceClassification
): boolean =>
  classification === PriceClassification.PerDiem ||
  classification === PriceClassification.EmployeeRate;

/**
 * How each classification reads in a sentence. Exhaustive over the enum on
 * purpose: a classification added later must be labelled here rather than
 * falling through to a generic string.
 */
export const classificationLabel: Record<PriceClassification, string> = {
  [PriceClassification.Fixed]: "Fixed price",
  [PriceClassification.Variable]: "Variable price",
  [PriceClassification.PerDiem]: "Per diem rate",
  [PriceClassification.EmployeeRate]: "Employee rate",
};

/**
 * Where each classification's amount comes from, for the product detail page.
 */
export const amountSource: Record<PriceClassification, string> = {
  [PriceClassification.Fixed]: "The amount comes from the rate you select.",
  [PriceClassification.Variable]:
    "The amount is determined when the charge is entered — from a receipt, a fuel purchase or an ad hoc quantity.",
  [PriceClassification.PerDiem]:
    "The amount comes from the per diem rate table, by county and effective date. A rate sheet may override it.",
  [PriceClassification.EmployeeRate]:
    "The amount comes from the employee rate table, by employee and effective date.",
};

export const currency = (amount?: number | null): string =>
  amount == null ? "—" : `$${amount.toFixed(2)}`;

/**
 * A per diem product's override rates: the non-null ones. Every product that is
 * not `Fixed` also carries exactly one null-amount sentinel, which is how a
 * rate with no override reaches the catalog -- not a price, and never listed.
 */
export const overrideRates = (product: Product): ProductRate[] =>
  product.rates.filter((x) => x.amount != null);

/**
 * The noun to use when a `ProductUnit` is read as a quantity -- "per hour",
 * "a single foot". Lowercasing the enum member does not work: `Hourly` is an
 * adverb and `Each` is a determiner, so both read wrongly in that position.
 */
export const unitNoun: Record<ProductUnit, string> = {
  [ProductUnit.Hourly]: "hour",
  [ProductUnit.Day]: "day",
  [ProductUnit.Each]: "unit",
  [ProductUnit.Foot]: "foot",
  [ProductUnit.Mile]: "mile",
  [ProductUnit.Gallon]: "gallon",
};
